/**
 * Fair Home V2 — Recent Searches for Search drawer (H13a → H14)
 *
 * Server-backed: `GET /search-history/get_history` (auth → ES, guest → session
 * cookie; merged on login). `POST /api/v1/search` writes the history itself, so
 * a submit needs no write here — only an optimistic row so the list moves
 * before the refetch lands. Clear = `DELETE /search-history` with every id.
 *
 * Photo tag ("Jacket" / "Whole outfit") is not on the wire — kept in a small
 * localStorage sidecar keyed by image basename. Rows from another device fall
 * back to "Whole outfit".
 */
import { computed, onMounted, ref, watch, type Ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useDeleteSearchHistoryMutation } from '@/mutations'
import { useSearchHistoryQuery } from '@/queries/search'
import {
  dedupeFairHomeV2SearchRecent,
  FAIR_HOME_V2_SEARCH_RECENT_MAX,
  FAIR_HOME_V2_SEARCH_RECENT_STORAGE_KEY,
  FAIR_HOME_V2_SEARCH_RECENT_TAGS_STORAGE_KEY,
  mapFairHomeV2SearchRecentFromServer,
  recentImageBasename,
  type FairHomeV2SearchRecentItem,
  type FairHomeV2SearchRecentTagMap,
} from './fair-home-v2-search-drawer.data'

function readTags(): FairHomeV2SearchRecentTagMap {
  if (typeof localStorage === 'undefined') return {}
  try {
    const raw = localStorage.getItem(FAIR_HOME_V2_SEARCH_RECENT_TAGS_STORAGE_KEY)
    if (!raw) return {}
    const parsed = JSON.parse(raw) as unknown
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {}
    const out: FairHomeV2SearchRecentTagMap = {}
    for (const [key, value] of Object.entries(parsed as Record<string, unknown>)) {
      if (typeof value === 'string' && value.trim()) out[key] = value
    }
    return out
  } catch {
    return {}
  }
}

function writeTags(tags: FairHomeV2SearchRecentTagMap) {
  if (typeof localStorage === 'undefined') return
  try {
    localStorage.setItem(FAIR_HOME_V2_SEARCH_RECENT_TAGS_STORAGE_KEY, JSON.stringify(tags))
  } catch {
    // quota / private mode — ignore
  }
}

function purgeLegacyStore() {
  if (typeof localStorage === 'undefined') return
  try {
    localStorage.removeItem(FAIR_HOME_V2_SEARCH_RECENT_STORAGE_KEY)
  } catch {
    // ignore
  }
}

export function useFairHomeV2SearchRecent(input: { enabled: Ref<boolean> }) {
  const { t } = useI18n()
  const tags = ref<FairHomeV2SearchRecentTagMap>({})
  /** Rows shown before the server confirms them (submit / clear). */
  const optimistic = ref<FairHomeV2SearchRecentItem[]>([])
  const clearedLocally = ref(false)

  const historyQuery = useSearchHistoryQuery(
    { size: FAIR_HOME_V2_SEARCH_RECENT_MAX, type: 'text' },
    input.enabled,
  )
  const deleteHistory = useDeleteSearchHistoryMutation()

  const serverItems = computed(() =>
    mapFairHomeV2SearchRecentFromServer(
      historyQuery.data.value?.recent_searches,
      tags.value,
      t('fair_home_v2.search.whole_outfit'),
    ),
  )

  const items = computed<FairHomeV2SearchRecentItem[]>(() => {
    const base = clearedLocally.value ? [] : serverItems.value
    return optimistic.value.reduceRight(
      (acc, next) => dedupeFairHomeV2SearchRecent(acc, next),
      base,
    )
  })

  onMounted(() => {
    purgeLegacyStore()
    tags.value = readTags()
  })

  // Reopen → fresh list (the preview's /v1/search calls have been writing).
  watch(
    () => input.enabled.value,
    (enabled) => {
      if (enabled) void historyQuery.refresh(true)
    },
  )

  // Server answered → optimistic rows are either in it or stale; drop them.
  watch(
    () => historyQuery.data.value,
    () => {
      optimistic.value = []
      clearedLocally.value = false
    },
  )

  function pushRecent(entry: Omit<FairHomeV2SearchRecentItem, 'id'> & { id?: string }) {
    const next: FairHomeV2SearchRecentItem = {
      id: entry.id ?? `local-${Date.now()}`,
      text: entry.text.trim(),
      photoUrl: entry.photoUrl,
      photoKey: entry.photoKey,
      photoTag: entry.photoTag,
    }
    if (!next.text && !next.photoTag) return
    const basename = recentImageBasename(next.photoKey ?? next.photoUrl)
    if (basename && next.photoTag) {
      tags.value = { ...tags.value, [basename]: next.photoTag }
      writeTags(tags.value)
    }
    clearedLocally.value = false
    optimistic.value = dedupeFairHomeV2SearchRecent(optimistic.value, next)
  }

  async function clearRecent() {
    const ids = serverItems.value.map((item) => item.id)
    optimistic.value = []
    clearedLocally.value = true
    if (ids.length === 0) return
    try {
      await deleteHistory.mutateAsync(ids)
    } finally {
      await historyQuery.refresh(true)
    }
  }

  return {
    items,
    loading: historyQuery.isLoading,
    pushRecent,
    clearRecent,
  }
}
